import React, { useState, useEffect } from "react";
import { ApiKeyOrGetter } from "../../services/GeneralService";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Checkbox } from "../ui/checkbox";
import { Skeleton } from "../ui/skeleton";
import { BillingService } from "../../services/BillingService";
import { PaymentMethod } from "../../model_gen/billing";
import { toast } from "sonner";

interface AutoTopupModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  apiKey: ApiKeyOrGetter;
  currentEnabled: boolean;
  currentThreshold: string;
  currentAmount: string;
  autopayCreditGranted: boolean;
}

const AutoTopupModal: React.FC<AutoTopupModalProps> = ({
  open,
  onClose,
  onSuccess,
  apiKey,
  currentEnabled,
  currentThreshold,
  currentAmount,
  autopayCreditGranted,
}) => {
  const [enabled, setEnabled] = useState<boolean>(currentEnabled);
  const [threshold, setThreshold] = useState<string>(currentThreshold);
  const [amount, setAmount] = useState<string>(currentAmount);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState<
    number | null
  >(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [showCustomAmount, setShowCustomAmount] = useState<boolean>(false);

  // Preset top-up amounts in credits
  const presetAmounts = [5000, 10000, 25000, 50000, 100000];

  // Conversion helpers
  const dollarsToCredits = (dollars: number): number => {
    return Math.round(dollars / 0.002);
  };

  const creditsToDollars = (credits: number): number => {
    return credits * 0.002;
  };

  const formatCredits = (credits: number): string => {
    return credits.toLocaleString();
  };

  useEffect(() => {
    if (open) {
      setEnabled(currentEnabled);
      // Convert dollar values to credits for display
      const thresholdDollars = parseFloat(currentThreshold) || 0;
      const amountDollars = parseFloat(currentAmount) || 0;
      const amountCredits = dollarsToCredits(amountDollars);
      setThreshold(dollarsToCredits(thresholdDollars).toString());
      setAmount(amountCredits.toString());
      // Check if the current amount is a preset or custom
      setShowCustomAmount(
        !presetAmounts.includes(amountCredits) && amountCredits > 0
      );
      fetchPaymentMethods();
    }
  }, [open, currentEnabled, currentThreshold, currentAmount]);

  const fetchPaymentMethods = () => {
    setLoading(true);
    BillingService.getPaymentMethods(null, apiKey).subscribe(
      (result: PaymentMethod[]) => {
        setPaymentMethods(result.filter((pm) => pm.status === "ENABLED"));

        // Find the auto-topup payment method or default
        const autoTopupMethod = result.find((pm) => pm.auto_topup_enabled);
        if (autoTopupMethod) {
          setSelectedPaymentMethodId(autoTopupMethod.id || null);
        } else {
          const defaultMethod = result.find((pm) => pm.is_default);
          if (defaultMethod) {
            setSelectedPaymentMethodId(defaultMethod.id || null);
          } else if (result.length > 0) {
            setSelectedPaymentMethodId(result[0].id || null);
          }
        }
        setLoading(false);
      },
      (error: any) => {
        console.error("Error fetching payment methods:", error);
        toast.error("Failed to load payment methods");
        setLoading(false);
      }
    );
  };

  const handleSave = () => {
    if (!enabled) {
      // Disable auto top-up
      const paymentMethodToUpdate =
        selectedPaymentMethodId ||
        paymentMethods.find((pm) => pm.auto_topup_enabled)?.id;

      if (paymentMethodToUpdate) {
        setSaving(true);
        BillingService.configureAutoTopup(
          paymentMethodToUpdate,
          false,
          0,
          0,
          null,
          apiKey
        ).subscribe(
          () => {
            toast.success("Auto top-up disabled");
            setSaving(false);
            onSuccess();
            onClose();
          },
          (error: any) => {
            console.error("Error disabling auto top-up:", error);
            toast.error("Failed to disable auto top-up");
            setSaving(false);
          }
        );
      }
      return;
    }

    // Validate inputs
    if (!selectedPaymentMethodId) {
      toast.error("Please select a payment method");
      return;
    }

    const thresholdCredits = parseFloat(threshold);
    const amountCredits = parseFloat(amount);

    if (isNaN(thresholdCredits) || thresholdCredits <= 0) {
      toast.error("Threshold must be greater than 0 credits");
      return;
    }

    if (isNaN(amountCredits) || amountCredits < 5000) {
      toast.error("Top-up amount must be at least 5,000 credits");
      return;
    }

    // Convert credits back to dollars for backend
    const thresholdDollars = creditsToDollars(thresholdCredits);
    const amountDollars = creditsToDollars(amountCredits);

    setSaving(true);

    BillingService.configureAutoTopup(
      selectedPaymentMethodId,
      true,
      thresholdDollars,
      amountDollars,
      null,
      apiKey
    ).subscribe(
      () => {
        toast.success("Auto top-up settings saved");
        setSaving(false);
        onSuccess();
        onClose();
      },
      (error: any) => {
        console.error("Error saving auto top-up:", error);
        toast.error("Failed to save auto top-up settings");
        setSaving(false);
      }
    );
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-gc-demi-bold">
            Auto recharge settings
          </DialogTitle>
        </DialogHeader>

        {!currentEnabled && !autopayCreditGranted && (
          <div className="px-4 py-3 bg-maitai-green/10 border border-maitai-green/20 rounded-lg">
            <p className="text-sm font-gc-med text-foreground">
              Set up autopay, get 2,500 credits
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Enable auto recharge and receive 2,500 credits to keep your
              service running smoothly.
            </p>
          </div>
        )}

        <div className="space-y-6 py-4">
          {/* Enable checkbox */}
          <div className="space-y-3">
            <Label className="text-sm font-gc-med text-foreground">
              Would you like to set up automatic recharge?
            </Label>
            <div className="flex items-start gap-3">
              <Checkbox
                id="enable-auto-topup"
                checked={enabled}
                onCheckedChange={(checked) => setEnabled(checked as boolean)}
              />
              <label
                htmlFor="enable-auto-topup"
                className="text-sm text-foreground cursor-pointer leading-tight">
                Yes, automatically recharge my card when my credit balance falls
                below a threshold
              </label>
            </div>
          </div>

          {enabled && (
            <>
              {/* Threshold */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">
                  When credit balance goes below
                </Label>
                <Input
                  type="number"
                  step="1"
                  min="1"
                  placeholder="5000"
                  value={threshold}
                  onChange={(e) => setThreshold(e.target.value)}
                  disabled={saving}
                />
                <p className="text-xs text-muted-foreground">
                  Trigger auto top-up when balance falls below this amount
                </p>
              </div>

              {/* Top-up Amount - Preset Options */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">
                  Add this many credits
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  {presetAmounts.map((preset) => (
                    <Button
                      key={preset}
                      type="button"
                      variant={
                        amount === preset.toString() && !showCustomAmount
                          ? "default"
                          : "outline"
                      }
                      size="sm"
                      onClick={() => {
                        setAmount(preset.toString());
                        setShowCustomAmount(false);
                      }}
                      disabled={saving}
                      className="flex flex-col items-center py-3 h-auto">
                      <span className="font-semibold">
                        {formatCredits(preset)}
                      </span>
                      <span className="text-xs opacity-70">
                        ${creditsToDollars(preset).toFixed(0)}
                      </span>
                    </Button>
                  ))}
                  {/* Other Button */}
                  <Button
                    type="button"
                    variant={showCustomAmount ? "default" : "outline"}
                    size="sm"
                    onClick={() => {
                      setShowCustomAmount(true);
                      setAmount("");
                    }}
                    disabled={saving}
                    className="flex flex-col items-center py-3 h-auto">
                    <span className="font-semibold">Other</span>
                    <span className="text-xs opacity-70">Custom</span>
                  </Button>
                </div>

                {/* Custom Amount Input - shown when "Other" is selected */}
                {showCustomAmount && (
                  <div className="space-y-1" style={{ marginTop: 12 }}>
                    <Input
                      type="number"
                      step="1000"
                      min="5000"
                      placeholder="Enter amount"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      disabled={saving}
                      autoFocus
                    />
                    {amount &&
                      parseFloat(amount) > 0 &&
                      parseFloat(amount) < 5000 && (
                        <p className="text-xs text-destructive">
                          Minimum amount is 5,000 credits
                        </p>
                      )}
                    {amount && parseFloat(amount) >= 5000 && (
                      <p className="text-xs text-muted-foreground">
                        ${creditsToDollars(parseFloat(amount)).toFixed(2)} will
                        be charged to your card
                      </p>
                    )}
                  </div>
                )}
              </div>

              {/* Payment method */}
              <div className="space-y-2">
                <Label className="text-sm font-gc-med text-foreground">
                  Payment method
                </Label>
                {loading ? (
                  <Skeleton className="h-10 w-full" />
                ) : paymentMethods.length > 0 ? (
                  <select
                    value={selectedPaymentMethodId || ""}
                    onChange={(e) =>
                      setSelectedPaymentMethodId(parseInt(e.target.value))
                    }
                    disabled={saving}
                    className="w-full h-10 px-3 text-sm rounded-md border border-border bg-background">
                    {paymentMethods.map((pm) => (
                      <option key={pm.id} value={pm.id || ""}>
                        {pm.meta.card?.brand} ••••{pm.meta.card?.last4}
                        {pm.is_default ? " (Default)" : ""}
                      </option>
                    ))}
                  </select>
                ) : null}
              </div>
            </>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving ? "Saving..." : "Save settings"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AutoTopupModal;
